import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Users, TrendingUp, Zap, AlertCircle, Download,
  Sparkles, Target, Clock, FileText, Image as ImageIcon, Headphones,
  Video, Presentation, ArrowUpRight, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGAGEMENT_META = {
  power_user:   { label: 'Power User',   color: '#7c3aed', bg: '#f3f0ff', icon: Sparkles },
  growing:      { label: 'Growing',      color: '#00A651', bg: '#ecfdf5', icon: TrendingUp },
  casual:       { label: 'Casual',       color: '#004ac6', bg: '#e5eeff', icon: Users },
  disengaged:   { label: 'Disengaged',   color: '#dc2626', bg: '#fef2f2', icon: AlertCircle },
  dormant:      { label: 'Dormant',      color: '#9ca3af', bg: '#f3f4f6', icon: Clock },
  pending:      { label: 'Pending',      color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  rejected:     { label: 'Rejected',     color: '#6b7280', bg: '#f3f4f6', icon: AlertCircle },
};

const TIER_META = {
  sovereign:      { label: 'SOVEREIGN',     color: '#7c3aed' },
  strategist:     { label: 'STRATEGIST',    color: '#00A651' },
  scout_complete: { label: 'SCOUT — 12/12', color: '#004ac6' },
  scout_partial:  { label: 'SCOUT — partial', color: '#64748b' },
  none:           { label: '—',             color: '#9ca3af' },
};

function formatDate(ts) {
  if (!ts) return '—';
  try {
    const d = typeof ts === 'string' ? new Date(ts) : ts;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function StatCard({ icon: Icon, label, value, sublabel, color = '#004ac6' }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border-t-4" style={{ borderTopColor: color }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#434655]">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '1A' }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-extrabold tracking-tighter text-[#0b1c30]">{value}</div>
      {sublabel && <p className="text-[11px] text-[#434655] mt-1">{sublabel}</p>}
    </div>
  );
}

export default function FleetHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [engagementFilter, setEngagementFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/admin/fleet-health`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load Fleet Health'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.fleet) return [];
    const q = search.trim().toLowerCase();
    return data.fleet.filter(u => {
      if (engagementFilter !== 'all' && u.engagement !== engagementFilter) return false;
      if (!q) return true;
      return (
        (u.email || '').toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.company || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, engagementFilter]);

  const handleExport = () => {
    if (!data?.fleet) return;
    const cols = ['email', 'full_name', 'company', 'status', 'engagement', 'max_tier',
      'countries_count', 'total_sections', 'credits_used_usd', 'days_since_activity',
      'last_login', 'upsell_signal'];
    const header = cols.join(',');
    const rows = data.fleet.map(u => cols.map(c => {
      const v = u[c] == null ? '' : String(u[c]).replace(/"/g, '""');
      return v.includes(',') || v.includes('"') ? `"${v}"` : v;
    }).join(','));
    const csv = '\ufeff' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleet-health-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen text-[#434655]"><Sparkles className="w-6 h-6 animate-pulse mr-2" /> Loading Fleet Health…</div>;
  }
  if (!data) return null;

  const totals = data.totals || {};
  const buckets = totals.engagement_buckets || {};

  return (
    <div data-testid="fleet-health-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="fleet"
        palette="emerald"
        eyebrow="Admin · per-user engagement"
        title="Fleet"
        accent="Health"
        subtitle="Per-user engagement, consumption, and upsell signals across the entire platform — powered by live usage telemetry."
        right={
          <Button onClick={handleExport} variant="outline" size="sm" className="gap-2 text-xs bg-white" data-testid="fleet-export-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        }
      />
      <div className="p-6 lg:p-8">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users} label="Total Users" value={totals.users || 0} sublabel={`${totals.active || 0} active`} color="#004ac6" />
        <StatCard icon={TrendingUp} label="Sections Generated" value={totals.total_sections || 0} sublabel="across all countries" color="#00A651" />
        <StatCard icon={Zap} label="Credits Used" value={`$${(totals.total_credits_usd || 0).toFixed(2)}`} sublabel="aggregate LLM spend" color="#f59e0b" />
        <StatCard icon={Target} label="Upsell Candidates" value={totals.upsell_candidates || 0} sublabel="awaiting nudge" color="#7c3aed" />
        <StatCard icon={Clock} label="Pending Review" value={totals.pending || 0} sublabel="need approval" color="#dc2626" />
      </div>

      {/* Engagement Distribution */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-8">
        <h2 className="text-sm font-bold tracking-tight mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#004ac6]" /> Engagement Distribution
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['power_user', 'growing', 'casual', 'disengaged', 'dormant'].map(key => {
            const meta = ENGAGEMENT_META[key];
            const Icon = meta.icon;
            const count = buckets[key] || 0;
            const pct = totals.active ? Math.round((count / totals.active) * 100) : 0;
            return (
              <div key={key} onClick={() => setEngagementFilter(engagementFilter === key ? 'all' : key)}
                   className={`rounded-lg p-3 cursor-pointer transition-all ${engagementFilter === key ? 'ring-2 ring-offset-2' : 'hover:shadow-md'}`}
                   style={{ backgroundColor: meta.bg, ringColor: meta.color }}
                   data-testid={`engagement-bucket-${key}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                </div>
                <div className="text-2xl font-extrabold tracking-tighter" style={{ color: meta.color }}>{count}</div>
                <div className="text-[10px] text-[#434655]">{pct}% of active</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#434655] shrink-0" />
        <Input placeholder="Search email, name, or company…" value={search}
               onChange={e => setSearch(e.target.value)}
               className="max-w-sm h-9 text-sm" data-testid="fleet-search-input" />
        <select value={engagementFilter} onChange={e => setEngagementFilter(e.target.value)}
                className="border border-[#c3c6d7]/40 rounded-md px-3 py-1.5 text-xs bg-white" data-testid="fleet-engagement-filter">
          <option value="all">All engagements</option>
          {Object.entries(ENGAGEMENT_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
        <span className="text-[11px] text-[#434655] ml-auto">Showing <strong className="text-[#0b1c30]">{filtered.length}</strong> of {data.fleet.length}</span>
      </div>

      {/* Fleet table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8f9ff] border-b border-[#c3c6d7]/30">
            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-[#434655]">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Engagement</th>
              <th className="px-4 py-3">Tier reached</th>
              <th className="px-4 py-3 text-right">Countries</th>
              <th className="px-4 py-3 text-right">Sections</th>
              <th className="px-4 py-3">AI Assets</th>
              <th className="px-4 py-3 text-right">Credits</th>
              <th className="px-4 py-3">Last activity</th>
              <th className="px-4 py-3">Upsell signal</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="9" className="px-4 py-12 text-center text-[#434655] text-sm">No users match the current filter.</td></tr>
            )}
            {filtered.map(u => {
              const em = ENGAGEMENT_META[u.engagement] || ENGAGEMENT_META.casual;
              const tm = TIER_META[u.max_tier] || TIER_META.none;
              return (
                <tr key={u.id} className="border-b border-[#c3c6d7]/20 hover:bg-[#f8f9ff]/50 transition-colors"
                    data-testid={`fleet-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[13px] text-[#0b1c30]">{u.full_name || u.email?.split('@')[0]}</div>
                    <div className="text-[11px] text-[#434655]">{u.email}</div>
                    {u.company && <div className="text-[10px] text-[#64748b] mt-0.5">{u.company}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ color: em.color, backgroundColor: em.bg }}>
                      {em.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: tm.color }}>{tm.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{u.countries_count}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-bold">{u.total_sections}</span>
                    <span className="text-[10px] text-[#434655]"> / {u.countries_count * 30}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-[10px] text-[#434655]">
                      {u.ai_assets.audio > 0       && <span title="Audio" className="flex items-center gap-0.5"><Headphones className="w-3 h-3 text-[#f59e0b]" />{u.ai_assets.audio}</span>}
                      {u.ai_assets.infographic > 0 && <span title="Infographic" className="flex items-center gap-0.5"><ImageIcon className="w-3 h-3 text-[#00A651]" />{u.ai_assets.infographic}</span>}
                      {u.ai_assets.video > 0       && <span title="Video" className="flex items-center gap-0.5"><Video className="w-3 h-3 text-[#7c3aed]" />{u.ai_assets.video}</span>}
                      {u.ai_assets.deck > 0        && <span title="Deck" className="flex items-center gap-0.5"><Presentation className="w-3 h-3 text-[#004ac6]" />{u.ai_assets.deck}</span>}
                      {u.ai_assets.pdf > 0         && <span title="PDF" className="flex items-center gap-0.5"><FileText className="w-3 h-3 text-[#dc2626]" />{u.ai_assets.pdf}</span>}
                      {!u.ai_assets.audio && !u.ai_assets.infographic && !u.ai_assets.video && !u.ai_assets.deck && !u.ai_assets.pdf && <span>—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px]">${u.credits_used_usd.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="text-[11px] text-[#0b1c30]">{formatDate(u.last_login)}</div>
                    {u.days_since_activity != null && (
                      <div className={`text-[10px] ${u.days_since_activity > 30 ? 'text-red-600' : u.days_since_activity > 7 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {u.days_since_activity === 0 ? 'today' : `${u.days_since_activity}d ago`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.upsell_signal ? (
                      <span className="text-[10px] font-semibold text-[#7c3aed] bg-[#f3f0ff] px-2 py-1 rounded-md inline-flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" />{u.upsell_signal}
                      </span>
                    ) : (
                      <span className="text-[10px] text-[#9ca3af]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
